/**
 * Canonical checkout flow definition.
 *
 * Framework-free (no React, no DOM, no fetch) declaration of the booking
 * checkout's section order, the controls that belong to each section, and the
 * gating predicates that decide when a control is shown / blocking. It is the
 * single source of truth that BOTH client surfaces must conform to:
 *
 *   - artifacts/bookaclassic (web)  — the reference implementation. It is NOT
 *     edited to import this module; the drift guard
 *     (scripts/check-checkout-flow-drift.cjs) statically asserts the web
 *     source still matches this definition.
 *   - artifacts/mobile (mobile)     — consumes this module directly so the
 *     mobile checkout renders the same sections, in the same order, with the
 *     same gating as web.
 *
 * The web flow is a single scrollable form (NOT a gated stepper): all sections
 * are visible top-to-bottom. The header step strip ("Review / Options /
 * Payment / Confirming") is a non-gating label only — the real runtime state is
 * just "form" | "confirming". Mobile must mirror this single-form structure.
 *
 * Layout note: web lays the left column (guest_details, payment) and the right
 * column (summary, which holds the inline trip editors) side by side on wide
 * screens. The `order` field below is the linear top-to-bottom order a
 * single-column surface (mobile) renders the sections in.
 */

export type CheckoutColumn = "left" | "right";

export type CheckoutSectionId = "guest_details" | "payment" | "summary";

export type CheckoutControlId =
  // guest_details
  | "phone_verification"
  | "occasion_picker"
  | "message_to_host"
  | "business_trip"
  // payment
  | "cancellation_policy"
  | "payment_timing"
  | "payment_method"
  | "insurance"
  | "promo_code"
  | "climate_offset"
  | "pay_button"
  // summary (right rail) — inline trip editors + price
  | "rental_type_editor"
  | "dates_editor"
  | "delivery_location_editor"
  | "price_breakdown";

export interface CheckoutSectionDef {
  /** Stable id. */
  readonly id: CheckoutSectionId;
  /** Web two-column placement. Mobile renders both columns in `order`. */
  readonly column: CheckoutColumn;
  /** Linear top-to-bottom render order (left column first, then right). */
  readonly order: number;
  /** Controls in their in-section render order. */
  readonly controls: readonly CheckoutControlId[];
}

/**
 * Ordered section definitions. Order matches the web render:
 *   left column  → guest_details, then payment
 *   right column → summary (sticky rail, holds trip editors + price)
 */
export const CHECKOUT_SECTIONS: readonly CheckoutSectionDef[] = [
  {
    id: "guest_details",
    column: "left",
    order: 1,
    controls: [
      "phone_verification",
      "message_to_host",
      "occasion_picker",
      "business_trip",
    ],
  },
  {
    id: "payment",
    column: "left",
    order: 2,
    controls: [
      "cancellation_policy",
      "payment_timing",
      "payment_method",
      "insurance",
      "promo_code",
      "climate_offset",
      "pay_button",
    ],
  },
  {
    id: "summary",
    column: "right",
    order: 3,
    controls: [
      "dates_editor",
      "rental_type_editor",
      "delivery_location_editor",
      "price_breakdown",
    ],
  },
] as const;

/** Section ids in render order. */
export const CHECKOUT_SECTION_ORDER: readonly CheckoutSectionId[] =
  CHECKOUT_SECTIONS.map((s) => s.id);

/** Controls in render order for one section, or [] if the id is unknown. */
export function controlsForSection(
  id: CheckoutSectionId,
): readonly CheckoutControlId[] {
  return CHECKOUT_SECTIONS.find((s) => s.id === id)?.controls ?? [];
}

/** The section a control belongs to, or null if the control is unknown. */
export function sectionForControl(
  control: CheckoutControlId,
): CheckoutSectionId | null {
  for (const section of CHECKOUT_SECTIONS) {
    if (section.controls.includes(control)) return section.id;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Gating predicates — pure, shared by both surfaces.
// ---------------------------------------------------------------------------

export const CHECKOUT_RENTAL_TYPES = ["driving", "chauffeured", "event"] as const;
export type CheckoutRentalType = (typeof CHECKOUT_RENTAL_TYPES)[number];

/**
 * The occasion picker is shown only for hourly trip types (chauffeur / event).
 * Self-drive renters are never asked what the trip is for.
 */
export function occasionPickerApplies(rentalType: CheckoutRentalType): boolean {
  return rentalType === "chauffeured" || rentalType === "event";
}

/**
 * Insurance/protection is offered only for self-drive trips in an insurable
 * market. Chauffeur / event trips carry the host's own cover, so the renter is
 * never charged for (or shown) a protection package on those.
 *
 * `insurableMarket` mirrors web's `checkoutIsInsurable` (server-derived from the
 * car's country + available packages); the client never decides insurability on
 * its own.
 */
export function insuranceApplies(args: {
  rentalType: CheckoutRentalType;
  insurableMarket: boolean;
}): boolean {
  return args.rentalType === "driving" && args.insurableMarket === true;
}

/**
 * The carbon-offset toggle is shown unless the feature flag is explicitly
 * disabled. An unknown / still-loading flag falls back to visible (matches
 * web's `featureFlags["climate-offset-checkout"] !== false`).
 */
export function climateOffsetApplies(args: { flagEnabled: boolean | undefined }): boolean {
  return args.flagEnabled !== false;
}

/**
 * The inline phone-verification panel blocks Pay while the quote's verification
 * matrix requires a verified phone and the renter has not yet verified. It is
 * always inline (OTP on the checkout surface) — never a redirect to settings.
 */
export function phoneGateBlocking(args: {
  requiresPhone: boolean;
  phoneVerified: boolean;
}): boolean {
  return args.requiresPhone === true && args.phoneVerified !== true;
}

// ---------------------------------------------------------------------------
// Hard constraints — documented as constants so the drift guard and both
// surfaces share one fact, not a copied comment.
// ---------------------------------------------------------------------------

/**
 * Driver licence / identity verification is collected POST-booking (task #625),
 * never as an in-checkout gate. Both surfaces must keep checkout free of any
 * licence/identity gate.
 */
export const LICENSE_GATE_IN_CHECKOUT = false as const;

/**
 * Every price shown at checkout comes from the server quote object. Clients
 * display quote fields only — no client-side price arithmetic (no
 * pricePerDay * days, no locally summed totals).
 */
export const CHECKOUT_PRICING_SOURCE = "server_quote" as const;

// ---------------------------------------------------------------------------
// Payment sequence — the create → confirm ordering both surfaces follow. The
// mobile surface swaps web's inline Stripe Elements for the native Payment
// Sheet, but the endpoint usage and ordering below must match.
// ---------------------------------------------------------------------------

export type CheckoutPaymentStep =
  | "submit_payment_details"
  | "prepare_booking"
  | "confirm_payment"
  | "await_result";

export const CHECKOUT_PAYMENT_SEQUENCE: readonly CheckoutPaymentStep[] = [
  "submit_payment_details",
  "prepare_booking",
  "confirm_payment",
  "await_result",
] as const;

/**
 * Every new booking prepares a payment-first session, then polls for the trip.
 * Existing-booking payment recovery is separate from new-booking creation.
 */
export const CHECKOUT_PREPARE_ENDPOINTS = {
  pfc: "/checkout-sessions-pfc",
} as const;

export type CheckoutBookingStep = "form" | "confirming";
